import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { merge, Observable, Subject } from 'rxjs'
import { filter, finalize, first, share, switchMap, takeUntil, tap } from 'rxjs/operators'

import { AuthService } from '../auth.service'
import { negativeBoolean } from '@core/helpers/negative-boolean'
import { AuthResponse } from '../auth-response.model'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { PlatformSettingsService } from '@core/services/platfrom-settings.service'
import { CLIENT_LANDING_URL } from '@core/constants/team-type'

@Component({
  selector: 'lin-verify-phone-number',
  templateUrl: './verify-phone-number.component.html',
  styleUrls: ['./verify-phone-number.component.scss'],
  standalone: false,
})
export class VerifyPhoneNumberComponent implements OnInit, OnDestroy {
  form: FormGroup
  formError: string
  onDestroy$ = new Subject<void>()
  isSubmitting = false
  isLoading = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorMessages: any
  verify: string
  token: string
  phoneNumber: string
  smsSend = false
  platformSettings$: Observable<PlatformSettings>

  @ViewChild('verifyCode') verifyCode: ElementRef<HTMLElement>

  constructor(
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private readonly platformSettingsService: PlatformSettingsService,
  ) {}

  ngOnInit() {
    this.createForm()

    this.platformSettings$ = this.platformSettingsService.platformSettings$
    this.token = <string>this.route.snapshot.params.token
    const { verifyPhone } = this.route.snapshot.params

    if (verifyPhone) {
      this.getPhoneNumber()
    } else {
      this.checkVerifyUserToken()
    }
  }

  getPhoneNumber() {
    this.authService.getPhoneNumber(this.token).subscribe((phone: string) => {
      this.phoneNumber = phone
      this.form.controls.phone.setValue(phone)
    })
  }

  checkVerifyUserToken() {
    const checkVerifyUserToken$ = this.authService.checkVerifyUserToken(this.token).pipe(share())

    const validToken$ = checkVerifyUserToken$.pipe(
      filter(Boolean),
      switchMap(() => this.authService.getPhoneNumber(this.token)),
      tap((phone: string) => {
        this.phoneNumber = phone
        this.form.controls.phone.setValue(phone)
      }),
    )

    const invalidToken$ = checkVerifyUserToken$.pipe(
      filter(negativeBoolean),
      tap(() => void this.router.navigate(['expired-link'])),
    )

    merge(validToken$, invalidToken$).subscribe(
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {},
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (error) => (this.formError = error.error.errors[0].detail.token as string),
    )
  }

  patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    /* eslint-disable */
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null
      }

      return regex.test(control.value) ? null : error
    }
    /* eslint-enable */
  }

  createForm() {
    this.form = this.formBuilder.group({
      phone: ['', [Validators.required, Validators.minLength(8), this.patternValidator(/^\+(?:[\d]*)$/, { phoneNumber: true })]],
      verificationCode: ['', [Validators.required, Validators.minLength(4)]],
    })

    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.onCreateFormValuesChanged())
  }

  onCreateFormValuesChanged() {
    this.formError = ''
  }

  sendSms() {
    const { phone } = this.form.value as { phone: string }
    this.authService
      .sendPhone(phone, this.token)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe(
        () => {
          this.smsSend = true
          this.verifyCode.nativeElement.focus()
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
        (error) => (this.formError = error.error.errors[0].detail as string),
      )
  }

  verifyPhoneNumber(): void {
    this.isSubmitting = true
    const { phone, verificationCode } = this.form.value as { phone: string; verificationCode: string }
    const verifyPhoneNumber$ = this.authService.verifyPhoneNumber(phone, verificationCode).pipe(
      finalize(() => (this.isSubmitting = false)),
      share(),
    )

    const redirect$ = verifyPhoneNumber$.pipe(
      filter((authResponse) => !!authResponse.token),
      switchMap((authResponse: AuthResponse) =>
        this.authService.setAuth(authResponse).pipe(
          tap(() => {
            // L30-1495: client users land on the v2 client app; admin and reseller keep their pages.
            const redirectUrl =
              authResponse.teamType === 'admin' ? 'admin' : authResponse.teamType === 'client' ? CLIENT_LANDING_URL : 'client'
            void this.router.navigate([redirectUrl])
          }),
        ),
      ),
    )

    const newPassword$ = verifyPhoneNumber$.pipe(
      filter((authResponse) => !authResponse.token),
      tap(() => void this.router.navigate(['set-new-password', this.token, { setNewPassword: true }])),
    )

    merge(redirect$, newPassword$)
      .pipe(first())
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        next: () => {},
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        error: (error) => (this.formError = <string>error.error.errors[0].detail),
      })
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
